import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import ExchangeImpersonationModal from "./ExchangeImpersonationModal";
import css from "./ExternalCalendarSyncPreferences.scss";
import { StaffProfilePrivileges, StaffNotificationPreferences } from 'utils/types';
import { getWord } from "utils/tenant";
import Icon from 'SF/MaterialIcon';
import Checkbox from 'SF/Checkbox';
import { isEmptyString } from 'utils/text';
import StyledButton from 'common/StyledButton';
import useStarfishAPI from "hooks/useStarfishAPI";

const ExchangeSyncPreferences = ({ exchangeAccounts, exchangeFreeBusyPopupLink, externalCalendarPreferenceSync, notificationPreferences, privileges, setValue }) => {
    const [isModalOpen, setModalOpen] = useState(false);
    // Represents if staff has accepted the opt in disclaimer in the exchange impersonation modal
    const [acceptedExchangeDisclaimer, setAcceptedExchangeDisclaimer] = useState(notificationPreferences.exchangeImpersonationEnabled);
    const [saveExchangeImpersonationResponse, callSaveExchangeImpersonationAPI] = useStarfishAPI();

    useEffect(() => {
        setAcceptedExchangeDisclaimer(notificationPreferences.exchangeImpersonationEnabled)
    }, [notificationPreferences]);

    const closeModal = () => {
        setAcceptedExchangeDisclaimer(notificationPreferences.exchangeImpersonationEnabled);
        setModalOpen(false);
    };

    const onImpersonationSave = () => {
        setModalOpen(false);
        callSaveExchangeImpersonationAPI('../instructor/saveExchangeImpersonationUserSettings.html', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                exchangeImpersonationEnabled: acceptedExchangeDisclaimer,
                actualExchangeFreeBusyEnabled: notificationPreferences.actualExchangeFreeBusyEnabled
            })
        });
    };

    useEffect(() => {
        if (saveExchangeImpersonationResponse.success) externalCalendarPreferenceSync(acceptedExchangeDisclaimer);
    }, [saveExchangeImpersonationResponse]);

    /* eslint-disable react/prop-types */
    const getButton = ({linkText, ariaLabel}) => {
        return (
            <div>
                <StyledButton asLink
                              aria-label={ariaLabel}
                              className={css['styled-button']}
                              onClick={() => setModalOpen(true)}>
                    {linkText}
                </StyledButton>
            </div>
        )
    };
    /* eslint-enable react/prop-types */

    const exchangeImpersonationTextForStatus = (exchangeImpStatus) => {
        if (exchangeImpStatus === 'SUCCESS') {
            return getButton({
                linkText: 'Click here to opt out',
                ariaLabel: "Click here to opt out of Outlook calendar sync"
            });
        } else if (exchangeImpStatus === 'FAILURE' || exchangeImpStatus === 'EXPIRED') {
            return (
                <>
                    {getButton({
                        linkText: 'Click here to try again',
                        ariaLabel: 'Click here to try to update opt-in status again'
                    })} Please
                    contact your Exchange administrator for further assistance.
                </>);
        } else if (exchangeImpStatus === 'PENDING') {
            return getButton({
                linkText: 'Click here to cancel your opt-in request',
                ariaLabel: 'Click here to cancel your Outlook calendar opt-in request'
            });
        } else {
            return `Status ${exchangeImpStatus} is not recognized. Please contact your Exchange Administrator.`;
        }
    };

    const getFreeBusyLink = (freeBusyPopupLink, linkName) => {
        return (
            <span dangerouslySetInnerHTML={{
                __html: `<a href="${freeBusyPopupLink}" aria-label="Click here for further instructions on ${linkName} sync" aria-haspopup="true">Click here</a>`
            }}/>
        );
    };

    const isOutLookSyncDisabled = () => {
        if (privileges.showExchangeImpersonationSection) {
            return !notificationPreferences.exchangeImpersonationEnabled;
        } else return isEmptyString(exchangeAccounts);
    };

    const getExchangeWebhookEnabledSection = () => {
        // if staff has already opted-in to exchange impersonation, then show the status and appropriate link
        if (notificationPreferences.exchangeImpersonationEnabled) {
            return (
                <div className={css['exchange-webhook-info']}>
                    <p data-testid="optInStatus">Your opt-in status is: <span
                        className={css['semi-bold']}>{notificationPreferences.exchangeImpersonationStatus}</span></p>
                    {
                        exchangeImpersonationTextForStatus(
                            notificationPreferences.exchangeImpersonationStatus.toUpperCase())
                    }
                </div>
            );
        }

        return (
            <div className={css['exchange-webhook-info']}>
                {getButton({
                    linkText: 'Click here to opt in',
                    ariaLabel: 'Click here to opt in to Outlook calendar sync'
                })}
            </div>
        );
    };

    const getExchangeAccountInfo = () => {
        // if tenant configs EXCHANGE_INTEGRATION_IMPERSONATION_ENABLED & EXCHANGE_INTEGRATION_IMPERSONATION_WEBHOOK_ENABLED
        // are enabled, then show the webhook section
        if (privileges.showExchangeImpersonationSection)
            return (
                <div className={css['warning-text']}>
                    <span className={css['important-text']}>Important:</span> You must opt-in to your
                    institution’s terms of service before {getWord('Starfish')} can read busy times from your Exchange
                    calendar.
                    Opt-ins are processed every 30 minutes.
                    {getExchangeWebhookEnabledSection()}
                </div>
            );

        if (!isEmptyString(exchangeAccounts)) {
            return (
                <div className={css['warning-text']}>
                    <Icon icon="warning" className={css['warning']}/>
                    <span className={css['important-text']}>Important:</span> You must share your calendar
                    with {exchangeAccounts}
                    <p>{getFreeBusyLink(exchangeFreeBusyPopupLink, 'outlook calendar')} for further instructions.</p>
                </div>);
        }

        return (
            <p>
                <Icon icon="warning" className={css['warning']}/>
                This feature cannot be enabled because your institution email account is not located
                on an Exchange server integrated with {getWord('Starfish')}.
            </p>);
    };

    return (
            <div className={css['external-cal-sync']}>
                <label className={css['external-sync-label']}>Outlook Calendar Sync</label>
                {getExchangeAccountInfo()}
                <div className={css['external-sync-checkbox']}>
                    <Checkbox id="outlookCal"
                              ariaLabel=""
                              disabled={isOutLookSyncDisabled()}
                              checked={!!notificationPreferences.exchangeFreeBusyEnabled}
                              onChange={(checked) => setValue({exchangeFreeBusyEnabled: checked})}/>
                    <label htmlFor="outlookCal" className={css['allow-busy-times-sync']}>
                        Allow {getWord('Starfish')} to read busy times <span className={css['semi-bold']}>from</span> my
                        Outlook Calendar</label>
                </div>
                <ExchangeImpersonationModal closeModal={closeModal}
                                            exchangeImpersonationEnabled={acceptedExchangeDisclaimer}
                                            exchangeImpersonationDisclaimer={privileges.exchangeImpersonationDisclaimer}
                                            isOpen={isModalOpen}
                                            onImpersonationOptInChange={setAcceptedExchangeDisclaimer}
                                            setValue={setValue}
                                            onImpersonationOptInSave={onImpersonationSave} />
            </div>
    );

};

ExchangeSyncPreferences.propTypes = {
    exchangeAccounts: PropTypes.string,
    exchangeFreeBusyPopupLink: PropTypes.string,
    externalCalendarPreferenceSync: PropTypes.func,
    hasSchedule: PropTypes.bool,
    notificationPreferences: StaffNotificationPreferences,
    privileges: StaffProfilePrivileges,
    setValue: PropTypes.func
};

export default ExchangeSyncPreferences;
